import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FormContainer from '../components/FormContainer';
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import zxcvbn from 'zxcvbn';

import { loginSuccess } from '../reducers/authReducer';
import { setLoading } from '../reducers/loadingReducer';
import { register, registrationStatus } from '../services/apiService';
import AbstractBackground from '../components/AbstractBackground';
import StaticBackground from '../components/StaticBackground';
import LoadingSpinner from '../components/LoadingSpinner';
import LogoTitle from '../components/LogoTitle';
import TermsOfUse from '../components/TermsOfUse';
import PasswordField from '../components/PasswordField';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';

const Register = () => {
  const theme = useTheme();
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [strengthFeedback, setStrengthFeedback] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(true);

  const loading = useSelector((state) => state.loading);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        dispatch(setLoading(true));
        const status = await registrationStatus();
        setIsRegistrationOpen(status.status);
      } catch (error) {
        console.error('Failed to check registration status:', error);
        setIsRegistrationOpen(false);
      } finally {
        dispatch(setLoading(false));
      }
    };
    checkRegistrationStatus();
  }, []);

  const showLogin = () => navigate('/login');

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    const strength = zxcvbn(newPassword);
    setStrengthFeedback(strength.feedback);
    setPasswordStrength(strength.score);
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    if (passwordStrength <= 2) return setError('Password is too weak.');
    if (!agreeToTerms) return setError('You must agree to the terms of use.');

    const formData = new FormData(event.target);
    const data = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      password: formData.get('password'),
    };

    dispatch(setLoading(true));
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const response = await register(data.email, data.password, data.lastName, data.firstName, dispatch);
      handleRegistrationSuccess(response);
      setError(null);
    } catch (error) {
      console.error('Failed to register:', error);
      setError(error.message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRegistrationSuccess = () => {
    setIsRegistered(true);
    setTimeout(() => navigate('/login'), 2000);
  };

  const memoizedBackground = useMemo(
    () => <AbstractBackground animate={loading} containerRef={containerRef} />, [loading]
  );

  const memoizedStaticBackground = useMemo(
    () => <StaticBackground numShapes={20} />, []
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute', // anchors absolute background
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        minHeight: '100vh',
        position: 'relative',
        padding: 2,


      }}
    >
      {memoizedBackground}
      {memoizedStaticBackground}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          m: 2,
          p: 2,
          borderRadius: 2,
          boxShadow: 2,
          animation: 'fadeIn 0.25s ease-in-out',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(to top, rgba(33,33,33,0.9), rgba(25,118,210,0.3))'
            : 'linear-gradient(to top, rgba(255,255,255,0.8), rgba(0,116,211,0.1))',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : isRegistered ? (
          <Box sx={{ ...formContainerStyle(theme) }}>
            <Button sx={logoStyle(theme)} onClick={showLogin} disabled={loading}>
              <LogoTitle />
            </Button>
            <Box sx={calloutStyle(theme)}>
              <Typography variant='h5'>Registration Successful!</Typography>
              <Typography variant='h6'>Check your email to verify your account.</Typography>
            </Box>
          </Box>
        ) : isRegistrationOpen ? (
          <Box component="form" onSubmit={handleRegister} sx={{ ...formContainerStyle(theme) }}>
            <Button sx={logoStyle(theme)} onClick={showLogin} disabled={loading}>
              <LogoTitle />
            </Button>
            <FormContainer sx={{ marginTop: 2 }}>
              <Typography variant="h6" mb={2}>Register</Typography>
              <TextField name="firstName" label="First Name" fullWidth margin="normal" required disabled={loading} />
              <TextField name="lastName" label="Last Name" fullWidth margin="normal" required disabled={loading} />
              <TextField name="email" label="Email" type="email" fullWidth margin="normal" required disabled={loading} />

              <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', marginTop: 2 }}>
                <PasswordField
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Password"
                  disabled={loading}
                />
                <PasswordStrengthIndicator passwordStrength={passwordStrength} strengthFeedback={strengthFeedback} />
              </Box>

              <FormControlLabel
                control={<Checkbox checked={agreeToTerms} onChange={(e) => setAgreeToTerms(e.target.checked)} disabled={loading} />}
                label={<Typography variant="body2">I agree to the <Button onClick={() => setTermsOpen(true)} disabled={loading}>terms of use</Button></Typography>}
              />

              {error && <Typography color="error" mt={2}>{error}</Typography>}

              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }}>Register</Button>
            </FormContainer>
          </Box>
        ) : (
          <Box sx={{ ...formContainerStyle(theme) }}>
            <Button sx={logoStyle(theme)} onClick={showLogin} disabled={loading}>
              <LogoTitle />
            </Button>
            <Box sx={calloutStyle(theme)}>
              <Typography variant="h6">Registration is closed.</Typography>
              <Typography variant="body2">Please check back soon!</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" mb={1}>Have an invitation code?</Typography>
              <Button variant="outlined" onClick={() => navigate('/register/invitation')}>Click here</Button>
            </Box>
          </Box>
        )}
      </Box>

      <Dialog open={termsOpen} onClose={() => setTermsOpen(false)}>
        <DialogTitle>Terms of Use</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '500px' }}>
          <TermsOfUse />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const formContainerStyle = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 2,
  padding: 3,
  maxWidth: 650,
  minHeight: 400,
  minWidth: 380,



  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(to top, rgba(33,33,33,0.8), rgba(0,116,211,0.2))'
      : 'linear-gradient(to top, rgba(255,255,255,0.8), rgba(0,116,211,0.1))',
  transition: 'background-color 0.3s ease',
  //boxShadow: theme.shadows[3],
});

const calloutStyle = (theme) => ({
  padding: 3,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 2,
  mt: 2,
  textAlign: 'center',
});

const logoStyle = (theme) => ({
  fontFamily: 'Arial, sans-serif',
  fontSize: 34,
  textTransform: 'none',
  fontWeight: 'bold',
  color: theme.palette.text.primary,
});

export default Register;
